// ✅ src/pages/Sales/PartPaymentReportAllPrint.jsx
// GET params from URL -> call Search API -> render data -> auto print
//
// Backend:
// app.use("/api/partListing", partPaymentListing);
//
// API used:
// GET /api/partListing/search?enquiryNumber=&customerName=&customerId=&assetSubType=&doB=&paymentStatus=

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const PartPaymentReportAllPrint = () => {
  const location = useLocation();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ read query params
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const formatNumber = (n) =>
    new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      Number(n || 0)
    );

  const formatDate = (d) => {
    const dt = d ? new Date(d) : new Date();
    if (isNaN(dt.getTime())) return "";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    return `${dd}/${mm}/${yy}`;
  };

  // ✅ call search API with SAME params
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);

        // Only allow these params (optional but safer)
        const allowed = ["enquiryNumber", "customerName", "customerId", "assetSubType", "doB", "paymentStatus"];
        const safeParams = new URLSearchParams();
        allowed.forEach((k) => {
          const v = queryParams.get(k);
          if (v !== null && String(v).trim() !== "") safeParams.set(k, v);
        });

        const res = await axios.get(`${BASE_URL}/api/partListing/search?${safeParams.toString()}`);
        const data = res?.data?.data ?? res?.data ?? [];
        console.log("data:", data);

        const arr = Array.isArray(data) ? data : [];

        // ✅ normalize API rows -> print table rows
        const mapped = arr.map((r, idx) => ({
          sNo: idx + 1,
          customerId: r?.customerId ?? r?.fld_customerid ?? "",
          customerName: r?.customerName ?? r?.fld_customername ?? "",
          plotNumber: r?.fld_plotnumber ?? r?.fld_plotno ?? "",
          assetCode: r?.fld_assetcode ?? r?.fld_assetcode ?? "",
          assetType: r?.fld_assetsubtypename ?? r?.assetType ?? r?.fld_assettypemodel ?? "",
          amount: Number(r?.fld_payingamount ?? r?.fld_amount ?? 0),
          balAmount: Number(r?.balAmount ?? r?.fld_balanceamount ?? 0),
          status: r?.fld_paystatus ?? r?.fld_paymentstatus ?? "",
        }));

        setReportData(mapped);

        // ✅ auto print after data render
        setTimeout(() => window.print(), 400);
      } catch (e) {
        console.error(e);
        setError("Failed to load report data");
        setReportData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [queryParams]);

  // ✅ total amount
  const totalAmount = useMemo(() => {
    return reportData.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [reportData]);

  const printedDate = formatDate(new Date());

  return (
    <div className="max-w-5xl mx-auto p-4 bg-white font-sans text-sm print:p-0 print:max-w-none">
      {/* Header Section */}
      <div className="border-2 border-black mb-0">
        <div className="border-b border-black flex">
          {/* Logo Section */}
          <div className="w-32 border-r border-black p-2 flex items-center justify-center">
            <div className="bg-black text-white px-2 py-1 text-xs font-bold text-center">
              <div>NISSI</div>
              <div>INFOTECH PVT LTD</div>
            </div>
          </div>

          {/* Company Info Section */}
          <div className="flex-1 p-3">
            <div className="text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-xs">
                <div>Nissi</div>
                <div className="font-bold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony, kavundampalayam,641630, Tamil Nadu, India.</div>
                <div>Phone No: 9003744644 infraconsoft-implementation@nissiinfotech.com</div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Title */}
        <div className="text-center py-2 border-b border-black font-bold">Part Payment Report</div>
      </div>

      {/* Loading/Error States */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div>{error}</div>
        </div>
      )}

      {/* Table Section */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black bg-gray-100">
              <th className="border-r border-black px-2 py-2 w-12 text-center">S.No</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Customer ID</th>
              <th className="border-r border-black px-2 py-2 w-28 text-center">Customer Name</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Plot Number</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Asset Code</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Asset Type / Model</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Amount</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Bal.Amount</th>
              <th className="px-2 py-2 w-20 text-center">Status</th>
            </tr>
          </thead>

          <tbody>
            {reportData.map((item, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-black px-2 py-2 text-center">{item.sNo}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.customerId}</td>
                <td className="border-r border-black px-2 py-2">{item.customerName}</td>
                <td className="border-r border-black px-2 py-2 text-center">{item.plotNumber}</td>
                <td className="border-r border-black px-2 py-2">{item.assetCode}</td>
                <td className="border-r border-black px-2 py-2">{item.assetType}</td>
                <td className="border-r border-black px-2 py-2 text-right">{formatNumber(item.amount)}</td>
                <td className="border-r border-black px-2 py-2 text-right">{formatNumber(item.balAmount)}</td>
                <td className="px-2 py-2 text-center">{item.status}</td>
              </tr>
            ))}

            {/* Empty rows to match screenshot layout (keep fixed height) */}
            {Array.from({ length: Math.max(0, 8 - reportData.length) }).map((_, index) => (
              <tr key={`empty-${index}`} className="border-b border-gray-300 h-8">
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="border-r border-black px-2 py-2"></td>
                <td className="px-2 py-2"></td>
              </tr>
            ))}

            {/* Total Row */}
            <tr className="border-t-2 border-black">
              <td colSpan="6" className="border-r border-black px-2 py-2"></td>
              <td className="border-r border-black px-2 py-2 text-right font-bold border-2 border-black">
                {formatNumber(totalAmount)}
              </td>
              <td colSpan="2" className="px-2 py-2"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex justify-end mt-8 text-xs">Printed Date: {printedDate}</div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          table {
            border-collapse: collapse !important;
          }
          th,
          td {
            border: 1px solid black !important;
          }
          .bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .bg-black {
            background-color: black !important;
            color: white !important;
          }
          @page {
            margin: 0.5in;
            size: A4 portrait;
          }
        }
      `}</style>
    </div>
  );
};

export default PartPaymentReportAllPrint;
